import { useEffect, useRef, useState } from 'react';
import { Download, Monitor, Scissors } from 'lucide-react';
import { isElectronMode } from '../../utils/apiMode';
import { editorBridge } from '../../services/editorBridge';
import { useTranslation } from '../../hooks/useTranslation';
import { TRANSLATION_PATH_PATTERN } from '../../config/i18n.config';

// VITE_EDITOR_DEV_URL points at the standalone Next.js dev server. It must
// only be honored in `npm run dev`; if it leaked into a production build
// (e.g. via .env.local), the iframe would request localhost:3001, fail with
// ERR_CONNECTION_REFUSED, and trigger Electron's did-fail-load reload —
// wiping the sidebar mode back to 'video'.
const devUrl = import.meta.env.DEV ? import.meta.env.VITE_EDITOR_DEV_URL : undefined;

// The OpenCut static export is bundled into `frontend/dist/video-editor/`
// only during the Electron packaging step (scripts/install/packaging/build.py).
// Cloud Run deploys do not ship that bundle, so anywhere outside Electron
// (and outside a local dev URL) we must show a download CTA instead of the
// iframe — otherwise the iframe 404s.
const isEditorBundleAvailable = (): boolean => Boolean(devUrl) || isElectronMode();

export function VideoEditorPage() {
  if (!isEditorBundleAvailable()) {
    return <VideoEditorDownloadCta />;
  }
  return <VideoEditorIframe />;
}

function VideoEditorIframe() {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [loading, setLoading] = useState(true);
  const { currentLanguage } = useTranslation();

  // In packaged Electron we load the iframe via a custom 'aigod-editor://'
  // scheme registered in scripts/install/packaging/electron/main.js. The
  // scheme is rooted at <resources>/frontend/dist/video-editor/, so the
  // Next.js export's absolute asset paths ('/_next/static/...') resolve
  // correctly — file:// would resolve them against the filesystem root
  // and 404 on every chunk.
  //
  // The 'app' host is a fixed placeholder, not a real path segment.
  // Because the scheme is registered with `standard: true`, Chromium's URL
  // parser requires a non-empty host; an empty host (aigod-editor:///...)
  // gets normalized by promoting the first path segment to the host slot,
  // which then makes the file handler look up the wrong file (the literal
  // 'editor/placeholder/index.html' becomes '/placeholder/index.html').
  const src = devUrl
    ? devUrl
    : isElectronMode()
      ? 'aigod-editor://app/editor/placeholder/index.html'
      : '/video-editor/editor/placeholder/index.html';

  useEffect(() => {
    editorBridge.setIframe(iframeRef.current);

    async function postTranslations(iframe: HTMLIFrameElement) {
      try {
        // In Electron (file://), an absolute "/locales/..." resolves to the
        // filesystem root and 404s. TRANSLATION_PATH_PATTERN already encodes
        // the right form ("./locales/{lang}.json" for file://, "/locales/..."
        // for serverless), matching the i18next loader.
        const localesPath = TRANSLATION_PATH_PATTERN.replace('{lang}', currentLanguage);
        const response = await fetch(localesPath);
        const all = response.ok ? await response.json() : null;
        const videoEditor = all?.videoEditor;
        if (!videoEditor) return;
        iframe.contentWindow?.postMessage(
          { type: 'AIGOD_I18N_UPDATE', translations: { videoEditor } },
          '*'
        );
      } catch (err) {
        console.warn('[VideoEditorPage] Failed to load editor translations', err);
      }
    }

    function onMessage(e: MessageEvent) {
      if (e.data?.type === 'AIGOD_EDITOR_READY') {
        setLoading(false);
        if (iframeRef.current) postTranslations(iframeRef.current);
      } else if (e.data?.type === 'AIGOD_I18N_REQUEST') {
        if (iframeRef.current) postTranslations(iframeRef.current);
      }
    }

    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('message', onMessage);
      editorBridge.setIframe(null);
    };
  }, [currentLanguage]);

  return (
    <div className="relative w-full h-full bg-black/30 rounded-lg overflow-hidden">
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center z-10 bg-black/50">
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-white/70 text-sm">Loading Video Editor...</span>
          </div>
        </div>
      )}
      <iframe
        ref={iframeRef}
        src={src}
        className="w-full h-full border-0"
        title="Video Editor"
        // `fullscreen` is required for the editor's preview fullscreen button:
        // requestFullscreen() inside an iframe is blocked by Permissions Policy
        // unless the host grants it here (the rejection is silent, so the button
        // just did nothing). allowFullScreen is the legacy alias some engines
        // still check.
        allow="clipboard-write; fullscreen"
        allowFullScreen
        onLoad={() => {
          setTimeout(() => setLoading(false), 5000);
        }}
      />
    </div>
  );
}

function VideoEditorDownloadCta() {
  const { t } = useTranslation();

  const goToDownload = () => {
    window.dispatchEvent(new CustomEvent('navigate-to-mode', { detail: 'download' }));
  };

  return (
    <div
      className="w-full h-full flex items-center justify-center px-3 sm:px-6 py-6 sm:py-10"
      data-testid="video-editor-download-cta"
    >
      <div className="w-full max-w-2xl bg-black/30 backdrop-blur-xl border border-white/10 rounded-2xl p-6 sm:p-10 flex flex-col items-center text-center">
        <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-purple-500/15 border border-purple-400/30 flex items-center justify-center mb-4">
          <Scissors className="w-7 h-7 sm:w-8 sm:h-8 text-purple-300" />
        </div>

        <h1 className="text-xl sm:text-2xl font-semibold text-white">
          {t('videoEditor.cloudCta.title', { defaultValue: 'Video Editor — Desktop only' })}
        </h1>

        <p className="mt-3 text-sm sm:text-base text-white/70 max-w-xl leading-relaxed">
          {t('videoEditor.cloudCta.description', {
            defaultValue:
              'The full timeline editor runs locally inside the desktop app, so your media never leaves your machine. Install Asgard Studio for your OS to unlock it.',
          })}
        </p>

        <div className="mt-5 flex items-center gap-2 text-xs sm:text-sm text-emerald-300/80">
          <Monitor className="w-4 h-4" />
          <span>
            {t('videoEditor.cloudCta.freeNote', {
              defaultValue: 'Free with the desktop app',
            })}
          </span>
        </div>

        <button
          type="button"
          onClick={goToDownload}
          className="mt-7 inline-flex items-center gap-2 px-5 sm:px-6 py-3 min-h-[44px] rounded-xl bg-purple-600 hover:bg-purple-500 text-white font-medium transition-colors shadow-lg shadow-purple-900/40"
          data-testid="video-editor-download-cta-button"
        >
          <Download className="w-4 h-4" />
          {t('videoEditor.cloudCta.downloadButton', {
            defaultValue: 'Get the desktop app',
          })}
        </button>

        <p className="mt-4 text-xs text-white/40">
          {t('videoEditor.cloudCta.platformsNote', {
            defaultValue: 'Available for Windows, macOS and Linux.',
          })}
        </p>
      </div>
    </div>
  );
}
